// Base translation service with shared caching and API logic
// Used by translationService, languageNameTranslator, and locationNameTranslator

import { dataStore } from '../stores/dataStore'

export interface TranslationCacheConfig {
  localStoragePrefix: string  // e.g., 'wital_translations_', 'wital_language_names_', 'wital_location_names_'
  cacheKeyPrefix: string      // e.g., 'translations_', 'language_names_', 'location_names_'
  serviceName: string         // e.g., 'Translation', 'Language Translator', 'Location Translator'
}

// AWS Translate API endpoint
const TRANSLATE_API_URL = '/api/translate'

// Cache expiry time (24 hours)
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000

export abstract class BaseTranslationService {
  protected targetLanguage: string = 'en'
  protected config: TranslationCacheConfig
  protected static translationCaches = new Map<string, Map<string, Record<string, string>>>()

  constructor(config: TranslationCacheConfig, targetLanguage: string = 'en') {
    this.config = config
    this.targetLanguage = targetLanguage
    
    // Initialize cache for this service if it doesn't exist
    if (!BaseTranslationService.translationCaches.has(config.cacheKeyPrefix)) {
      BaseTranslationService.translationCaches.set(config.cacheKeyPrefix, new Map())
    }
  }

  // Set the target language for translations
  setTargetLanguage(language: string): void {
    this.targetLanguage = language
  }

  // Get current target language
  getTargetLanguage(): string {
    return this.targetLanguage
  }

  // Get the cache for this service
  protected getCache(): Map<string, Record<string, string>> {
    return BaseTranslationService.translationCaches.get(this.config.cacheKeyPrefix)!
  }

  // Get localStorage and cache keys for current language
  protected getKeys() {
    return {
      localStorageKey: `${this.config.localStoragePrefix}${this.targetLanguage}`,
      cacheKey: `${this.config.cacheKeyPrefix}${this.targetLanguage}`
    }
  }

  // Check if translation is needed (skip for English)
  protected isTranslationNeeded(): boolean {
    return this.targetLanguage !== 'en'
  }

  // Get cached translations or fetch from API
  protected async getTranslations(textsToTranslate: Record<string, string>): Promise<Record<string, string>> {
    if (!this.isTranslationNeeded()) {
      return textsToTranslate // Return original texts for English
    }

    const { localStorageKey, cacheKey } = this.getKeys()
    const cache = this.getCache()
    
    // Check in-memory cache first
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey)
      if (cached && typeof cached === 'object') {
        const missingTexts = Object.keys(textsToTranslate).filter(key => !cached.hasOwnProperty(key))
        
        if (missingTexts.length === 0) {
          console.log(`📋 ${this.config.serviceName}: Using in-memory cache for ${this.targetLanguage}`)
          return this.filterRequestedTranslations(cached, textsToTranslate)
        }
      }
    }

    // Check localStorage cache
    const cachedData = localStorage.getItem(localStorageKey)
    const cacheTimestamp = localStorage.getItem(`${localStorageKey}_timestamp`)
    
    if (cachedData && cacheTimestamp) {
      try {
        const cachedTranslations = JSON.parse(cachedData)
        const now = Date.now()
        
        // Check if cached translations are less than 24 hours old AND have all current texts
        const missingTexts = Object.keys(textsToTranslate).filter(key => !cachedTranslations.hasOwnProperty(key))
        
        if ((now - parseInt(cacheTimestamp)) < CACHE_EXPIRY_MS && missingTexts.length === 0) {
          console.log(`📚 ${this.config.serviceName}: Using cached translations for ${this.targetLanguage}`)
          cache.set(cacheKey, cachedTranslations)
          return this.filterRequestedTranslations(cachedTranslations, textsToTranslate)
        } else if (missingTexts.length > 0) {
          console.log(`🔄 ${this.config.serviceName}: Cached translations missing ${missingTexts.length} texts, refreshing...`)
        } else {
          console.log(`⏰ ${this.config.serviceName}: Cache expired, refreshing...`)
        }
      } catch (error) {
        console.warn(`${this.config.serviceName}: Failed to parse cached translations:`, error)
        localStorage.removeItem(localStorageKey)
        localStorage.removeItem(`${localStorageKey}_timestamp`)
      }
    }

    // Fetch translations from API
    return await this.fetchTranslationsFromAPI(textsToTranslate, localStorageKey, cacheKey)
  }

  // Filter translations to only return what was requested
  protected filterRequestedTranslations(
    allTranslations: Record<string, string>, 
    requestedTexts: Record<string, string>
  ): Record<string, string> {
    const result: Record<string, string> = {}
    for (const key of Object.keys(requestedTexts)) {
      result[key] = allTranslations[key] || requestedTexts[key] // Fallback to original if not found
    }
    return result
  }

  // Fetch translations from AWS Lambda
  protected async fetchTranslationsFromAPI(
    textsToTranslate: Record<string, string>,
    localStorageKey: string,
    cacheKey: string
  ): Promise<Record<string, string>> {
    
    // Check which translations we already have cached (incremental translation)
    let actualTextsToTranslate: Record<string, string> = {}
    const cachedData = localStorage.getItem(localStorageKey)
    
    if (cachedData) {
      try {
        const cachedTranslations = JSON.parse(cachedData)
        
        // Only translate texts that are missing from cache
        for (const [key, text] of Object.entries(textsToTranslate)) {
          if (!cachedTranslations[key]) {
            actualTextsToTranslate[key] = text
          }
        }
        
        console.log(`🔧 ${this.config.serviceName}: Found ${Object.keys(textsToTranslate).length} total texts, ${Object.keys(actualTextsToTranslate).length} need translation`)
      } catch (error) {
        console.warn(`${this.config.serviceName}: Failed to parse cached translations, will translate all texts`)
        actualTextsToTranslate = textsToTranslate
      }
    } else {
      // No cache, translate everything
      actualTextsToTranslate = textsToTranslate
      console.log(`🔧 ${this.config.serviceName}: No cache found, translating all ${Object.keys(actualTextsToTranslate).length} texts`)
    }
    
    // If no texts need translation, return cached data
    if (Object.keys(actualTextsToTranslate).length === 0) {
      console.log(`🔧 ${this.config.serviceName}: All texts already cached, using existing translations`)
      const cachedTranslations = JSON.parse(cachedData!)
      this.getCache().set(cacheKey, cachedTranslations)
      return this.filterRequestedTranslations(cachedTranslations, textsToTranslate)
    }

    // Create the API request
    const formData = new URLSearchParams()
    formData.append('targetLanguage', this.targetLanguage)
    formData.append('texts', JSON.stringify(actualTextsToTranslate))
    
    console.log(`🌐 ${this.config.serviceName}: Fetching translations for ${Object.keys(actualTextsToTranslate).length} texts...`)
    
    const response = await fetch(TRANSLATE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Translation API error: ${response.status} ${response.statusText} - ${errorText}`)
    }

    const data = await response.json()
    const newTranslations = data.translations || data
    
    // Merge with existing cached translations if any
    let finalTranslations = newTranslations
    if (cachedData) {
      try {
        const existingTranslations = JSON.parse(cachedData)
        finalTranslations = { ...existingTranslations, ...newTranslations }
        console.log(`🔧 ${this.config.serviceName}: Merged ${Object.keys(newTranslations).length} new translations with ${Object.keys(existingTranslations).length} existing ones`)
      } catch (error) {
        console.warn(`${this.config.serviceName}: Failed to merge with existing translations, using only new ones`)
        finalTranslations = newTranslations
      }
    }

    // Cache the translations
    this.getCache().set(cacheKey, finalTranslations)
    
    // Store in localStorage with timestamp
    localStorage.setItem(localStorageKey, JSON.stringify(finalTranslations))
    localStorage.setItem(`${localStorageKey}_timestamp`, Date.now().toString())

    console.log(`✅ ${this.config.serviceName}: Translations cached for ${this.targetLanguage}`)
    
    // Return only the translations for the requested texts
    return this.filterRequestedTranslations(finalTranslations, textsToTranslate)
  }

  // Clear cache for a specific language
  clearCache(languageCode?: string): void {
    const targetLang = languageCode || this.targetLanguage
    const localStorageKey = `${this.config.localStoragePrefix}${targetLang}`
    const cacheKey = `${this.config.cacheKeyPrefix}${targetLang}`
    
    // Clear in-memory cache
    this.getCache().delete(cacheKey)
    
    // Clear localStorage cache
    localStorage.removeItem(localStorageKey)
    localStorage.removeItem(`${localStorageKey}_timestamp`)
    
    console.log(`🗑️ ${this.config.serviceName}: Cleared cache for ${targetLang}`)
  }

  // Clear all caches for this service
  clearAllCaches(): void {
    // Clear in-memory cache
    this.getCache().clear()
    
    // Clear localStorage cache for all languages
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(this.config.localStoragePrefix)) {
        localStorage.removeItem(key)
      }
    })
    
    console.log(`🗑️ ${this.config.serviceName}: Cleared all caches`)
  }

  // Static method to clear all translation caches across all services
  static clearAllServiceCaches(): void {
    // Clear all in-memory caches
    BaseTranslationService.translationCaches.clear()
    
    // Clear all localStorage translation caches
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith('wital_translations_') || 
          key.startsWith('wital_language_names_') || 
          key.startsWith('wital_location_names_')) {
        localStorage.removeItem(key)
      }
    })
    
    console.log('🗑️ BaseTranslationService: Cleared all translation caches across all services')
  }
} 